import { Search } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { UNITS } from "../../../config/config";
import {
  Category,
  Product,
  Station,
} from "../../../data/models/product/ProductModel";

interface ProductsTableProps {
  products: Product[];
  categories: Category[];
  stations: Station[];
  deleteProduct: (id: string) => Promise<void>;
  onEdit: (product: Product) => void;
}

export default function ProductsTable({
  products,
  categories,
  stations,
  deleteProduct,
  onEdit,
}: ProductsTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [search, setSearch] = useState("");
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const getCategory = (id: string) =>
    categories.find((c) => c.id === id)?.name ?? "-";

  const getUnit = (id: string) => UNITS.find((c) => c.id === id)?.name ?? "-";

  const getStation = (id: string) =>
    stations.find((c) => c.id === id)?.name ?? "-";

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const text = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(text) ||
        getCategory(p.categoryId).toLowerCase().includes(text) ||
        p.unit.toLowerCase().includes(text)
      );
    });
  }, [products, search]);

  const handleDelete = async (id: string) => {
    setLoadingId(id);
    await deleteProduct(id);
    setLoadingId(null);
  };

  const columns: TableColumn<Product>[] = [
    {
      name: "Nombre",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Categoría",
      selector: (row) => getCategory(row.categoryId),
      sortable: true,
    },
    {
      name: "Medida",
      selector: (row) => getUnit(row.unit),
      sortable: true,
    },
    {
      name: "Pantallas",
      cell: (row) => {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {row.stations?.length > 0
              ? row.stations.map((id) => (
                  <Chip
                    key={id}
                    label={getStation(id)}
                    size="small"
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.text.primary,
                    }}
                  />
                ))
              : "—"}
          </Box>
        );
      },
      sortable: false,
      grow: 2,
    },
    {
      name: "Cant. mínima",
      selector: (row) => row.minQuantity.toString(),
      right: true,
    },
    {
      name: "Costo",
      selector: (row) => `$${row.cost.toLocaleString()}`,
      right: true,
      sortable: true,
    },
    {
      name: "Precio venta",
      selector: (row) => `$${row.salePrice.toLocaleString()}`,
      right: true,
      sortable: true,
    },
    {
      name: "Acciones",
      cell: (row) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => onEdit(row)}
            disabled={loadingId !== null}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.id!)}
            disabled={loadingId !== null}
          >
            {loadingId === row.id ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        overflowX: "auto",
        padding: 2,
        marginY: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Productos
      </Typography>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <TextField
          size="small"
          label="Buscar producto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DataTable
        fixedHeader
        fixedHeaderScrollHeight="400px"
        columns={columns}
        data={filteredProducts}
        pagination
        highlightOnHover
        striped
        noDataComponent="No hay productos para mostrar"
        paginationComponentOptions={{
          rowsPerPageText: "Filas por página",
          rangeSeparatorText: "de",
          selectAllRowsItem: false,
        }}
        customStyles={{
          noData: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          },
          table: {
            style: {
              width: isMobile ? "80vw" : "100%",
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
            },
          },
          headRow: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          },
          headCells: {
            style: {
              fontWeight: 600,
              fontSize: "14px",
              color: theme.palette.text.primary,
            },
          },
          rows: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              minHeight: "48px",
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            stripedStyle: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              minHeight: "48px",
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            highlightOnHoverStyle: {
              backgroundColor: theme.palette.divider,
              border: "transparent",
              color: theme.palette.text.primary,
              transitionDuration: "0.2s",
              transitionProperty: "background-color",
              cursor: "pointer",
            },
          },
          pagination: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      />
    </Card>
  );
}
